import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Form, Input, Modal, Switch } from 'antd';
import { safeUrlRule } from '@shared/utils/safeUrl';
import type { SliderImage, SliderImageInput } from '../types';
import { fromRange, toRange, type RangeValue } from '../utils/dateRange';

const { RangePicker } = DatePicker;

interface FormValues {
  imageUrl: string;
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
  range?: RangeValue;
  isActive: boolean;
}

interface SliderImageFormModalProps {
  open: boolean;
  editing: SliderImage | null;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (input: SliderImageInput, editingId: string | null) => void;
}

export function SliderImageFormModal({
  open,
  editing,
  saving,
  onCancel,
  onSubmit,
}: SliderImageFormModalProps) {
  const { t } = useTranslation(['widgets', 'common', 'validation']);
  const [form] = Form.useForm<FormValues>();
  const isEdit = editing !== null;

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      imageUrl: editing?.imageUrl ?? '',
      title: editing?.title ?? '',
      subtitle: editing?.subtitle ?? '',
      ctaText: editing?.ctaText ?? '',
      ctaLink: editing?.ctaLink ?? '',
      range: toRange(editing?.startDate, editing?.endDate) ?? undefined,
      isActive: editing?.isActive ?? true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const onOk = () => {
    void (async () => {
      try {
        const v = await form.validateFields();
        const { startDate, endDate } = fromRange(v.range ?? null);
        onSubmit(
          {
            imageUrl: v.imageUrl.trim(),
            title: v.title?.trim() || null,
            subtitle: v.subtitle?.trim() || null,
            ctaText: v.ctaText?.trim() || null,
            ctaLink: v.ctaLink?.trim() || null,
            startDate,
            endDate,
            isActive: v.isActive,
          },
          editing?.id ?? null,
        );
      } catch {
        // inline validation
      }
    })();
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t('image.editTitle') : t('image.createTitle')}
      onCancel={onCancel}
      onOk={onOk}
      okText={isEdit ? t('common:actions.save') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={saving}
      destroyOnClose
      maskClosable={!saving}
      width={560}
    >
      <Form<FormValues> form={form} layout="vertical">
        <Form.Item name="imageUrl" label={t('fields.imageUrl')} rules={[{ required: true, message: t('image.imageUrlRequired') }, safeUrlRule(t('validation:safeUrl'))]}>
          <Input maxLength={500} placeholder={t('fields.imageUrlPlaceholder')} />
        </Form.Item>
        <Form.Item name="title" label={t('fields.title')}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="subtitle" label={t('fields.subtitle')}>
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item name="ctaText" label={t('fields.ctaText')}>
          <Input maxLength={80} />
        </Form.Item>
        <Form.Item name="ctaLink" label={t('fields.ctaLink')} rules={[safeUrlRule(t('validation:safeUrl'))]}>
          <Input maxLength={500} placeholder={t('fields.ctaLinkPlaceholder')} />
        </Form.Item>
        <Form.Item name="range" label={t('fields.dateRange')} help={t('fields.dateRangeHint')}>
          <RangePicker showTime style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" />
        </Form.Item>
        <Form.Item name="isActive" label={t('fields.isActive')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
